import { CellValue } from '../types.js';
import type { LineDeduction, SolverLine } from './types.js';

/**
 * Trivial line: clue is [0] (all empty) or sum of clues fills entire line.
 */
export function trivialLine(
  line: SolverLine,
  clues: number[]
): LineDeduction[] {
  const deductions: LineDeduction[] = [];
  const len = line.length;

  // All-empty clue
  if (clues.length === 1 && clues[0] === 0) {
    for (let i = 0; i < len; i++) {
      if (line[i] === CellValue.Unknown) {
        deductions.push({ index: i, value: CellValue.Empty });
      }
    }
    return deductions;
  }

  // Sum of clues + minimum gaps = line length → fully determined
  const totalFilled = clues.reduce((a, b) => a + b, 0);
  const minLength = totalFilled + clues.length - 1;
  if (minLength === len) {
    let pos = 0;
    for (let g = 0; g < clues.length; g++) {
      for (let i = 0; i < clues[g]; i++) {
        if (line[pos] === CellValue.Unknown) {
          deductions.push({ index: pos, value: CellValue.Filled });
        }
        pos++;
      }
      if (g < clues.length - 1) {
        if (line[pos] === CellValue.Unknown) {
          deductions.push({ index: pos, value: CellValue.Empty });
        }
        pos++;
      }
    }
    return deductions;
  }

  return deductions;
}

/**
 * Simple overlap: compute leftmost and rightmost placements for each clue group,
 * then cells in the overlap region must be filled, and cells that no group can
 * reach must be empty.
 */
export function simpleOverlap(
  line: SolverLine,
  clues: number[]
): LineDeduction[] {
  if (clues.length === 1 && clues[0] === 0) return [];

  const len = line.length;
  const numGroups = clues.length;

  const leftmost = computeLeftmost(line, clues);
  if (!leftmost) return [];

  const rightmost = computeRightmost(line, clues);
  if (!rightmost) return [];

  const deductions: LineDeduction[] = [];

  // For each group, overlap between leftmost and rightmost → filled
  for (let g = 0; g < numGroups; g++) {
    const lStart = leftmost[g];
    const rStart = rightmost[g];
    const groupLen = clues[g];

    // Overlap region: max(lStart, rStart) to min(lStart+groupLen, rStart+groupLen) - 1
    const overlapStart = Math.max(lStart, rStart);
    const overlapEnd = Math.min(lStart + groupLen, rStart + groupLen);

    for (let i = overlapStart; i < overlapEnd; i++) {
      if (line[i] === CellValue.Unknown) {
        deductions.push({ index: i, value: CellValue.Filled });
      }
    }
  }

  // Cells that no group can reach in any valid placement → empty
  const covered = new Uint8Array(len);
  for (let g = 0; g < numGroups; g++) {
    const earliest = leftmost[g];
    const latest = rightmost[g] + clues[g];
    for (let i = earliest; i < latest; i++) {
      covered[i] = 1;
    }
  }
  for (let i = 0; i < len; i++) {
    if (!covered[i] && line[i] === CellValue.Unknown) {
      deductions.push({ index: i, value: CellValue.Empty });
    }
  }

  return deductions;
}

/**
 * Edge logic: filled cells touching the edges constrain group placement.
 * If the first/last filled cell is close to the wall, we can deduce cells.
 */
export function edgeLogic(
  line: SolverLine,
  clues: number[]
): LineDeduction[] {
  if (clues.length === 1 && clues[0] === 0) return [];

  const len = line.length;
  const deductions: LineDeduction[] = [];

  // Left edge: if cell at position i is filled and i < clues[0],
  // then all cells from 0..i must be filled, and cell at clues[0] (if in range) is empty
  const firstClue = clues[0];
  for (let i = 0; i < firstClue; i++) {
    if (line[i] === CellValue.Filled) {
      // Extend fill from i to firstClue-1
      for (let j = i; j < firstClue; j++) {
        if (line[j] === CellValue.Unknown) {
          deductions.push({ index: j, value: CellValue.Filled });
        }
      }
      // Cell after first clue group must be empty (if we know it starts from 0)
      if (i === 0 && firstClue < len && line[firstClue] === CellValue.Unknown) {
        deductions.push({ index: firstClue, value: CellValue.Empty });
      }
      break;
    }
    if (line[i] === CellValue.Empty) break;
  }

  // Right edge: mirror logic for last clue
  const lastClue = clues[clues.length - 1];
  for (let i = len - 1; i >= len - lastClue; i--) {
    if (line[i] === CellValue.Filled) {
      for (let j = i; j >= len - lastClue; j--) {
        if (line[j] === CellValue.Unknown) {
          deductions.push({ index: j, value: CellValue.Filled });
        }
      }
      if (i === len - 1 && len - lastClue - 1 >= 0 && line[len - lastClue - 1] === CellValue.Unknown) {
        deductions.push({ index: len - lastClue - 1, value: CellValue.Empty });
      }
      break;
    }
    if (line[i] === CellValue.Empty) break;
  }

  return deductions;
}

/**
 * Space filling: find segments of non-Empty cells bounded by Empty cells
 * or line boundaries. If a segment contains no Filled cells and is too small
 * for the smallest clue, mark all unknowns in it as empty.
 */
export function spaceFilling(
  line: SolverLine,
  clues: number[]
): LineDeduction[] {
  if (clues.length === 1 && clues[0] === 0) return [];

  const len = line.length;
  const deductions: LineDeduction[] = [];
  const minClue = Math.min(...clues);

  // Find segments bounded by Empty cells or line edges
  let segStart = -1;
  let hasFilled = false;

  for (let i = 0; i <= len; i++) {
    const cell = i < len ? line[i] : CellValue.Empty; // sentinel: treat end as Empty
    if (cell === CellValue.Empty) {
      if (segStart >= 0) {
        const segLen = i - segStart;
        // Only mark as empty if segment has no Filled cells and is too small
        if (!hasFilled && segLen < minClue) {
          for (let j = segStart; j < i; j++) {
            if (line[j] === CellValue.Unknown) {
              deductions.push({ index: j, value: CellValue.Empty });
            }
          }
        }
      }
      segStart = -1;
      hasFilled = false;
    } else {
      if (segStart < 0) {
        segStart = i;
        hasFilled = false;
      }
      if (cell === CellValue.Filled) hasFilled = true;
    }
  }

  return deductions;
}

// --- Placement helpers ---

/**
 * Compute leftmost valid start position for each clue group,
 * respecting already-known cells. Uses recursive search with
 * backtracking to correctly handle filled cells that constrain placement.
 * Returns null if no valid placement exists (contradiction).
 */
export function computeLeftmost(
  line: SolverLine,
  clues: number[]
): number[] | null {
  const len = line.length;
  const n = clues.length;
  const positions = new Array<number>(n);

  function place(g: number, minStart: number): boolean {
    if (g === n) {
      // All groups placed — check no filled cells remain uncovered
      const from = g > 0 ? positions[g - 1] + clues[g - 1] : 0;
      for (let i = from; i < len; i++) {
        if (line[i] === CellValue.Filled) return false;
      }
      return true;
    }

    const prevEnd = g > 0 ? positions[g - 1] + clues[g - 1] : 0;
    const groupLen = clues[g];

    for (let s = minStart; s + groupLen <= len; s++) {
      // Gap check: no Filled cells in [prevEnd, s-1] (they'd be uncovered)
      let gapOk = true;
      for (let j = prevEnd; j < s; j++) {
        if (line[j] === CellValue.Filled) {
          gapOk = false;
          break;
        }
      }
      if (!gapOk) return false; // Can't skip past filled cells

      // Group check: no Empty cells in [s, s+groupLen-1]
      let groupOk = true;
      for (let j = s; j < s + groupLen; j++) {
        if (line[j] === CellValue.Empty) {
          groupOk = false;
          // Skip ahead past this empty cell
          s = j;
          break;
        }
      }
      if (!groupOk) continue;

      // Separator check: cell after group must not be Filled
      if (s + groupLen < len && line[s + groupLen] === CellValue.Filled) {
        continue;
      }

      positions[g] = s;
      if (place(g + 1, s + groupLen + 1)) return true;
    }

    return false;
  }

  return place(0, 0) ? positions : null;
}

/**
 * Compute rightmost valid start position for each clue group.
 * Mirror of leftmost — places groups right-to-left.
 * Returns null if no valid placement exists (contradiction).
 */
export function computeRightmost(
  line: SolverLine,
  clues: number[]
): number[] | null {
  const len = line.length;
  const n = clues.length;
  const positions = new Array<number>(n);

  function place(g: number, maxEnd: number): boolean {
    if (g < 0) {
      // All groups placed — check no filled cells before first group
      const to = n > 0 ? positions[0] : len;
      for (let i = 0; i < to; i++) {
        if (line[i] === CellValue.Filled) return false;
      }
      return true;
    }

    const nextStart = g < n - 1 ? positions[g + 1] : len;
    const groupLen = clues[g];

    for (let s = maxEnd - groupLen; s >= 0; s--) {
      // Gap check: no Filled cells in [s+groupLen, nextStart-1]
      let gapOk = true;
      for (let j = s + groupLen; j < nextStart; j++) {
        if (line[j] === CellValue.Filled) {
          gapOk = false;
          break;
        }
      }
      if (!gapOk) return false; // Can't skip past filled cells

      // Group check: no Empty cells in [s, s+groupLen-1]
      let groupOk = true;
      for (let j = s; j < s + groupLen; j++) {
        if (line[j] === CellValue.Empty) {
          groupOk = false;
          // Skip back before this empty cell
          s = j - groupLen + 1;
          break;
        }
      }
      if (!groupOk) continue;

      // Separator check: cell before group must not be Filled
      if (s > 0 && line[s - 1] === CellValue.Filled) {
        continue;
      }

      positions[g] = s;
      if (place(g - 1, s - 1)) return true;
    }

    return false;
  }

  return place(n - 1, len) ? positions : null;
}
